import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Upload, FileText, CheckCircle } from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import SiteBackground from '../components/SiteBackground';
import api from '../utils/api';

const ApplicationFormPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  
  const recruitmentId = searchParams.get('job_id');
  const recruitmentTitle = searchParams.get('job_title');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  
  const [resumeFile, setResumeFile] = useState(null);
  const [coverLetterFile, setCoverLetterFile] = useState(null);
  const [resumeUrl, setResumeUrl] = useState('');
  const [coverLetterUrl, setCoverLetterUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // ページ読み込み時に画面の一番上にスクロール
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleFileUpload = async (file, type) => {
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/applications/upload-resume', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const fileUrl = response.data.url;
      
      if (type === 'resume') {
        setResumeUrl(fileUrl);
        toast({
          title: '履歴書をアップロードしました',
          description: file.name,
        });
      } else {
        setCoverLetterUrl(fileUrl);
        toast({
          title: '職務経歴書をアップロードしました',
          description: file.name,
        });
      }
    } catch (error) {
      toast({
        title: 'アップロードエラー',
        description: error.response?.data?.detail || 'ファイルのアップロードに失敗しました',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!recruitmentId || !recruitmentTitle) {
      toast({
        title: 'エラー',
        description: '採用情報が見つかりません',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);

    try {
      await api.post('/applications', {
        recruitment_id: recruitmentId,
        recruitment_title: recruitmentTitle,
        ...formData,
        resume_url: resumeUrl || null,
        cover_letter_url: coverLetterUrl || null,
      });

      setSubmitted(true);
      
      toast({
        title: '応募完了',
        description: 'ご応募ありがとうございます。担当者より連絡させていただきます。',
      });

      // 3秒後にホームページにリダイレクト
      setTimeout(() => {
        navigate('/');
      }, 3000);

    } catch (error) {
      toast({
        title: '送信エラー',
        description: error.response?.data?.detail || '応募の送信に失敗しました',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-gradient-to-br from-gray-50 to-gray-100 border-amber-500/30">
          <CardContent className="pt-12 pb-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">応募完了</h2>
            <p className="text-gray-600 mb-2">ご応募ありがとうございます。</p>
            <p className="text-gray-600 mb-6">担当者より順次ご連絡させていただきます。</p>
            <Button
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-amber-500 to-amber-600"
            >
              ホームに戻る
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-gray-900 relative">
      <SiteBackground />
      <SiteHeader activePath="/apply" />

      {/* メインコンテンツ */}
      <main className="pt-40 pb-20 px-4 relative">
        <div className="max-w-3xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-6 text-amber-500 hover:text-amber-500"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            戻る
          </Button>

          <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-amber-500/30">
            <CardHeader>
              <CardTitle className="text-2xl bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                求人応募フォーム
              </CardTitle>
              <p className="text-gray-600 mt-2">
                応募先: <span className="text-gray-900 font-semibold">{recruitmentTitle}</span>
              </p>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* お名前 */}
                <div>
                  <Label htmlFor="name" className="text-gray-600">
                    お名前 <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-2 bg-white/50 border-amber-500/30 text-gray-900"
                    placeholder="山田 太郎"
                  />
                </div>

                {/* メールアドレス */}
                <div>
                  <Label htmlFor="email" className="text-gray-600">
                    メールアドレス <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-2 bg-white/50 border-amber-500/30 text-gray-900"
                    placeholder="example@email.com"
                  />
                </div>

                {/* 電話番号 */}
                <div>
                  <Label htmlFor="phone" className="text-gray-600">
                    電話番号 <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="mt-2 bg-white/50 border-amber-500/30 text-gray-900"
                    placeholder="090-1234-5678"
                  />
                </div>

                {/* 志望動機 */}
                <div>
                  <Label htmlFor="message" className="text-gray-600">
                    志望動機・自己PR <span className="text-red-400">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="mt-2 bg-white/50 border-amber-500/30 text-gray-900 min-h-[150px]"
                    placeholder="志望動機や自己PRをご記入ください"
                  />
                </div>

                {/* 履歴書アップロード */}
                <div>
                  <Label className="text-gray-600">
                    履歴書 <span className="text-gray-500">(任意)</span>
                  </Label>
                  <div className="mt-2">
                    <input
                      type="file"
                      id="resume"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        setResumeFile(file);
                        handleFileUpload(file, 'resume');
                      }}
                      className="hidden"
                    />
                    <label
                      htmlFor="resume"
                      className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-amber-500/30 rounded-lg cursor-pointer hover:border-amber-500/50 transition-colors"
                    >
                      <Upload className="w-5 h-5 text-amber-500 mr-2" />
                      <span className="text-gray-600">
                        {resumeFile ? resumeFile.name : 'ファイルを選択 (PDF, Word, 画像)'}
                      </span>
                    </label>
                  </div>
                </div>

                {/* 職務経歴書アップロード */}
                <div>
                  <Label className="text-gray-600">
                    職務経歴書 <span className="text-gray-500">(任意)</span>
                  </Label>
                  <div className="mt-2">
                    <input
                      type="file"
                      id="coverLetter"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        setCoverLetterFile(file);
                        handleFileUpload(file, 'coverLetter');
                      }}
                      className="hidden"
                    />
                    <label
                      htmlFor="coverLetter"
                      className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-amber-500/30 rounded-lg cursor-pointer hover:border-amber-500/50 transition-colors"
                    >
                      <FileText className="w-5 h-5 text-amber-500 mr-2" />
                      <span className="text-gray-600">
                        {coverLetterFile ? coverLetterFile.name : 'ファイルを選択 (PDF, Word, 画像)'}
                      </span>
                    </label>
                  </div>
                </div>

                {/* 送信ボタン */}
                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={submitting || uploading}
                    className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-gray-900 font-semibold py-3"
                  >
                    {submitting ? '送信中...' : uploading ? 'アップロード中...' : '応募する'}
                  </Button>
                </div>

                <p className="text-sm text-gray-500 text-center">
                  応募いただいた情報は採用選考のみに使用します
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default ApplicationFormPage;
